import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CheckboxDropdownSelect.css';

export type CDSOption = { value: string; label: string };

export type CheckboxDropdownSelectProps = {
  id: string;
  placeholder?: string;
  options: CDSOption[];
  value: CDSOption[];
  onChange: (next: CDSOption[]) => void;

  enableSearch?: boolean;
  searchPlaceholder?: string;

  /**
   * 「すべて（表示中）」トグル
   * - 連名者: true
   * - それ以外: false
   */
  showSelectAll?: boolean;
  selectAllLabel?: string; // default: 'すべて'
  optionDescriptions?: Record<
    string,
    { summaryText: string; tooltipText: string }
  >;
  defaultOptionDescription?: { summaryText: string; tooltipText: string };
  summaryVisibleCount?: number;
};

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();

const toOption = (x: any): CDSOption => {
  if (typeof x === 'string' || typeof x === 'number') {
    const s = norm(x);
    return { value: s, label: s };
  }
  const value = norm(x?.value ?? x?.label ?? x?.name ?? x?.text ?? '');
  const label = norm(x?.label ?? x?.name ?? x?.text ?? x?.value ?? value);
  return { value, label };
};

const uniqueOptions = (opts: CDSOption[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const v = norm(o?.value);
    const l = norm(o?.label);
    if (!v && !l) return false;
    const k = `${v}__${l}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const sigOf = (arr: CDSOption[]) =>
  (arr ?? [])
    .map((x) => norm(x.value))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'ja'))
    .join('|');

const CheckboxDropdownSelect: React.FC<CheckboxDropdownSelectProps> = React.memo(
  ({
    id,
    placeholder,
    options,
    value,
    onChange,
    enableSearch = true,
    searchPlaceholder = 'キーワードで絞り込み',
    showSelectAll = false,
    selectAllLabel = 'すべて',
    optionDescriptions,
    defaultOptionDescription,
    summaryVisibleCount = 3,
  }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');

    // 親が追いつくまで UI を巻き戻さない
    const pendingSigRef = useRef<string>('');

    // 外クリックで閉じる
    useEffect(() => {
      if (typeof document === 'undefined') return;
      const h = (e: MouseEvent | TouchEvent) => {
        const el = e.target as Node | null;
        if (rootRef.current && el && !rootRef.current.contains(el)) setOpen(false);
      };
      document.addEventListener('mousedown', h);
      document.addEventListener('touchstart', h);
      return () => {
        document.removeEventListener('mousedown', h);
        document.removeEventListener('touchstart', h);
      };
    }, []);

    const tokens = useMemo(
      () =>
        String(keyword ?? '')
          .normalize('NFKC')
          .trim()
          .split(/\s+/)
          .filter(Boolean),
      [keyword]
    );

    const normalizedOptions = useMemo(() => uniqueOptions((options ?? []).map(toOption)), [options]);

    const filtered = useMemo(() => {
      if (!enableSearch || tokens.length === 0) return normalizedOptions;

      return normalizedOptions.filter((o) => {
        const lv = norm(o.label);
        const vv = norm(o.value);
        return tokens.every((t) => lv.includes(t) || vv.includes(t));
      });
    }, [normalizedOptions, tokens, enableSearch]);

    // 検索中は「表示中（filtered）」のみが “すべて” の対象
    const target = keyword ? filtered : normalizedOptions;

    // value を options に寄せて正規化（value/label どっちで来ても吸収）
    const placeholderKey = useMemo(() => norm(placeholder ?? ''), [placeholder]);

    const canonicalizeSelected = useCallback(
      (raw: any): CDSOption[] => {
        const picked = Array.isArray(raw)
          ? raw
              .map((v: any) => ({ value: norm(v?.value), label: norm(v?.label) }))
              .filter((x: any) => x.value || x.label)
          : [];

        const byValue = new Map<string, CDSOption>();
        const byLabel = new Map<string, CDSOption>();
        normalizedOptions.forEach((o) => {
          byValue.set(norm(o.value), o);
          const l = norm(o.label);
          if (l && !byLabel.has(l)) byLabel.set(l, o);
        });

        const out: CDSOption[] = [];
        const seen = new Set<string>();

        picked.forEach((s: any) => {
          const v = norm(s.value);
          const l = norm(s.label);
          if (placeholderKey && (v === placeholderKey || l === placeholderKey)) return;

          const hit = byValue.get(v) || byValue.get(l) || byLabel.get(l) || byLabel.get(v);
          const next = hit ? { value: hit.value, label: hit.label } : { value: v || l, label: l || v || (v || l) };

          if (!next.value) return;
          if (seen.has(next.value)) return;
          seen.add(next.value);
          out.push(next);
        });

        return out;
      },
      [normalizedOptions, placeholderKey]
    );

    const [localSelected, setLocalSelected] = useState<CDSOption[]>(() => canonicalizeSelected(value));

    // props(value/options) が変わったら同期。ただし pending 中は巻き戻さない
    useEffect(() => {
      const next = canonicalizeSelected(value);
      const nextSig = sigOf(next);

      if (pendingSigRef.current) {
        if (pendingSigRef.current !== nextSig) return;
        pendingSigRef.current = '';
      }

      const curSig = sigOf(localSelected);
      if (curSig !== nextSig) setLocalSelected(next);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, normalizedOptions]);

    const selectedSet = useMemo(() => new Set(localSelected.map((v) => v.value)), [localSelected]);

    const optionsMap = useMemo(() => new Map(normalizedOptions.map((o) => [o.value, o.label])), [normalizedOptions]);

    const apply = useCallback(
      (vals: string[]) => {
        const next = vals.map((v) => ({ value: v, label: optionsMap.get(v) ?? v }));

        setLocalSelected(next);
        pendingSigRef.current = sigOf(next);
        onChange(next);
      },
      [onChange, optionsMap]
    );

    const toggleOne = useCallback(
      (v: string) => {
        const s = new Set(selectedSet);
        s.has(v) ? s.delete(v) : s.add(v);
        apply(Array.from(s));
      },
      [selectedSet, apply]
    );

    const allSelectedForTarget =
      showSelectAll && target.length > 0 && target.every((o) => selectedSet.has(o.value));
    const someSelectedForTarget =
      showSelectAll && !allSelectedForTarget && target.some((o) => selectedSet.has(o.value));

    const selectAllRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (!selectAllRef.current) return;
      selectAllRef.current.indeterminate = !!someSelectedForTarget;
    }, [someSelectedForTarget]);

    const toggleAll = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        e.stopPropagation();
        if (!showSelectAll) return;
        if (target.length === 0) return;

        if (allSelectedForTarget) {
          // 表示中のみ解除（対象外の選択は保持）
          const targetSet = new Set(target.map((o) => o.value));
          const keep = Array.from(selectedSet).filter((v) => !targetSet.has(v));
          apply(keep);
          return;
        }

        // 表示中を全部追加（既存も保持）
        const add = new Set<string>(Array.from(selectedSet));
        target.forEach((o) => add.add(o.value));
        apply(Array.from(add));
      },
      [showSelectAll, target, allSelectedForTarget, selectedSet, apply]
    );

    const hasExtraSelected = useMemo(() => {
      const optionValueSet = new Set((normalizedOptions ?? []).map((o) => norm(o.value)));
      return (localSelected ?? []).some((s) => !optionValueSet.has(norm(s.value)));
    }, [normalizedOptions, localSelected]);

    const summaryState = useMemo<{
      text: string;
      fullText: string;
      hasValue: boolean;
    }>(() => {
      const size = selectedSet.size;
      if (!size) {
        return { text: placeholder ?? '', fullText: '', hasValue: false };
      }

      const optionValueSet = new Set((normalizedOptions ?? []).map((o) => norm(o.value)));

      const summarizeLabels = (labels: string[]) => {
        const limit = Math.max(1, Math.floor(summaryVisibleCount));
        return {
          text:
            labels.length > limit
              ? `${labels.slice(0, limit).join(', ')}…`
              : labels.join(', '),
          fullText: labels.join('、'),
          hasValue: labels.length > 0,
        };
      };

      if (size === normalizedOptions.length && normalizedOptions.length > 0 && !hasExtraSelected) {
        const labelsAll = normalizedOptions.map((o) => o.label);
        if (showSelectAll) {
          return {
            text: selectAllLabel,
            fullText: labelsAll.join('、'),
            hasValue: true,
          };
        }

        return summarizeLabels(labelsAll);
      }

      const labelsFromOptions = normalizedOptions.filter((o) => selectedSet.has(o.value)).map((o) => o.label);
      const labelsFromCustom = (localSelected ?? [])
        .filter((s) => !optionValueSet.has(norm(s.value)))
        .map((s) => s.label || s.value)
        .filter((lb) => norm(lb) !== '');
      const labels = [...labelsFromOptions, ...labelsFromCustom].filter((lb) => norm(lb) !== '');

      if (labels.length === 0) {
        return { text: placeholder ?? '', fullText: '', hasValue: false };
      }
      return summarizeLabels(labels);
    }, [
      selectedSet,
      normalizedOptions,
      placeholder,
      showSelectAll,
      selectAllLabel,
      localSelected,
      hasExtraSelected,
      summaryVisibleCount,
    ]);

    return (
      <div ref={rootRef} className="cds" data-open={open ? 'true' : 'false'}>
        <button
          type="button"
          className="cds__control"
          title={summaryState.fullText || undefined}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`cds__placeholder ${summaryState.hasValue ? 'is-filled' : ''}`}>
            {summaryState.text || placeholder || ''}
          </span>
          <span className="cds__arrow" aria-hidden="true" />
        </button>

        {open && (
          <div className="cds__menu" role="listbox" aria-multiselectable="true">
            {enableSearch && (
              <div className="cds__search">
                <input
                  type="text"
                  aria-label="filter"
                  placeholder={searchPlaceholder}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setKeyword('');
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  className="cds__searchInput"
                />
              </div>
            )}

            {showSelectAll && (
              <label className="cds__option cds__option--all">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="cds__checkbox"
                  checked={allSelectedForTarget}
                  onChange={toggleAll}
                />
                <span>
                  {selectAllLabel}
                  {keyword ? '（表示中）' : ''}
                </span>
              </label>
            )}

            <div className="cds__scroll">
              {filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                const description =
                  optionDescriptions?.[o.value] ?? defaultOptionDescription;
                return (
                  <label
                    key={`${id}__${o.value}`}
                    className="cds__option"
                    aria-selected={checked}
                    title={description?.tooltipText}
                  >
                    <input
                      type="checkbox"
                      className="cds__checkbox"
                      checked={checked}
                      onChange={() => toggleOne(o.value)}
                    />
                    <span className="cds__optionText">
                      <span className="cds__optionLabel">{o.label}</span>
                      {description?.summaryText && (
                        <span className="cds__optionScope">
                          {description.summaryText}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}

              {filtered.length === 0 && <div className="cds__nohit">該当する候補がありません</div>}
            </div>
          </div>
        )}
      </div>
    );
  }
);

CheckboxDropdownSelect.displayName = 'CheckboxDropdownSelect';
export default CheckboxDropdownSelect;
